import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, firstValueFrom } from 'rxjs';
import { DeliverynoteModel } from './deliverynote.model';
import { AccountsAndTaxesModel } from './accountsandtaxes.model';
import { environment } from '../../../../environments/environment';
import { ResultModel } from '../../../shared/result.model';
import { ApiResponse } from '../../../models/base';

const LIST_ENDPOINT = '/preinvoices';

interface ApiMessageUppercaseResponse {
  Message: string;
}

@Injectable({
  providedIn: 'root',
})
export class DeliveryNoteService {
  deliverynoteList: DeliverynoteModel[] = [];

  private readonly http = inject(HttpClient);
  private readonly saveRequestSubject = new BehaviorSubject<boolean>(false);
  public readonly saveRequestObservable$: Observable<boolean> =
    this.saveRequestSubject.asObservable();

  public saveRequestUpdateValue(value: boolean): void {
    this.saveRequestSubject.next(value);
  }

  public async pdf(uid: string, clientUid: string): Promise<Blob> {
    const response = await firstValueFrom(
      this.http.get(environment.apiUrl + `/pdf/${uid}/${clientUid}`, {
        responseType: 'blob',
      })
    );

    return new Blob([response], { type: 'application/pdf' });
  }

  public manualPublishRecurrent(): Promise<boolean> {
    return firstValueFrom(
      this.http.get<boolean>(environment.apiUrl + '/incomes/recurrents/start')
    );
  }

  public async accountsAndTaxesData(): Promise<AccountsAndTaxesModel> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<AccountsAndTaxesModel>>(environment.apiUrl + '/accounting')
    );

    return response.data;
  }

  public async addData(preinvoice: DeliverynoteModel): Promise<DeliverynoteModel> {
    preinvoice.isDeliveryNote = true;

    const response = await firstValueFrom(
      this.http.post<ApiResponse<DeliverynoteModel>>(
        environment.apiUrl + LIST_ENDPOINT,
        preinvoice
      )
    );

    return response.data;
  }

  public async deleteData(uid: string): Promise<string> {
    const response = await firstValueFrom(
      this.http.delete<ApiMessageUppercaseResponse>(
        environment.apiUrl + `${LIST_ENDPOINT}/${uid}`
      )
    );

    return response.Message;
  }

  public async editData(
    uid: string,
    preinvoice: DeliverynoteModel
  ): Promise<DeliverynoteModel> {
    const response = await firstValueFrom(
      this.http.put<ApiResponse<DeliverynoteModel>>(
        environment.apiUrl + `${LIST_ENDPOINT}/${uid}`,
        preinvoice
      )
    );

    return response.data;
  }

  public async loadData(uid: string): Promise<DeliverynoteModel> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<DeliverynoteModel>>(
        environment.apiUrl + `/preinvoice/${uid}`
      )
    );

    return response.data;
  }

  public async getList(
    offset?: number,
    limit?: number
  ): Promise<ResultModel<DeliverynoteModel>> {
    let params = new HttpParams();

    if (offset !== undefined) {
      params = params.set('offset', offset.toString());
    }

    if (limit !== undefined) {
      params = params.set('limit', limit.toString());
    }

    const response = await firstValueFrom(
      this.http.get<ApiResponse<ResultModel<DeliverynoteModel>>>(
        environment.apiUrl + LIST_ENDPOINT + '/deliverynote',
        { params }
      )
    );

    return response.data;
  }

  public async getTotal(): Promise<number> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<number>>(
        environment.apiUrl + LIST_ENDPOINT + '/deliverynote/total'
      )
    );

    return response.data;
  }

  public async duplicateDeliveryNotes(uid: string): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<boolean>>(environment.apiUrl + `/deliverynotes/duplicate/${uid}`)
    );

    return response.data;
  }
}